import { useEffect, useState } from "react";
import { TypewriterText } from "./TypewriterText";

type Props = {
  questionText: string | null;
  streamingQuestion?: string;
  answerText: string | null;
  /** True while we're writing/streaming the question (cursor still blinking on Q) */
  questionLive: boolean;
  /** True while we're showing the just-recorded answer (slid up under question) */
  showingAnswer: boolean;
  /** "you said" | "you wrote" */
  answerLabel?: string;
  /** Optional live interim transcript shown UNDER the recording button (separate slot). */
  /** Replay the question's spoken audio. When omitted the control is hidden. */
  onReplay?: () => void;
  /** True while TTS is currently playing — shows a soft indicator instead of the replay button. */
  isSpeaking?: boolean;
};

/**
 * Composes the question and the most recent answer onto a single page. The
 * answer slides up from below the question with an ease, then both fade as the
 * next question takes over. The question stays in the same vertical slot the
 * whole time so the eye doesn't have to chase it around.
 */
export function QuestionAndAnswer({
  questionText,
  streamingQuestion,
  answerText,
  questionLive,
  showingAnswer,
  answerLabel = "you said",
  onReplay,
  isSpeaking = false,
}: Props) {
  const live = Boolean(streamingQuestion && (!questionText || streamingQuestion.length > 0));
  const displayQuestion = live ? (streamingQuestion ?? "") : (questionText ?? "");

  // Track whether answer has appeared so we can animate its first reveal.
  const [answerVisible, setAnswerVisible] = useState(false);
  useEffect(() => {
    if (showingAnswer && answerText) {
      const t = window.setTimeout(() => setAnswerVisible(true), 30);
      return () => window.clearTimeout(t);
    } else {
      setAnswerVisible(false);
    }
  }, [showingAnswer, answerText]);

  if (!questionText && !streamingQuestion) {
    return (
      <div className="flex items-center justify-center min-h-[260px]" aria-live="polite">
        <span className="sr-only">Composing the next question…</span>
        <ThinkingDots />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center px-2 w-full" aria-live="polite">
      {/* Question — fixed vertical slot */}
      <div className="relative max-w-2xl">
        <h1
          className={`font-hand text-5xl sm:text-6xl leading-[1.18] tracking-[-0.005em] transition-colors duration-700 ${
            showingAnswer ? "text-ink-500" : "text-ink-900"
          }`}
        >
          <TypewriterText text={displayQuestion} isLive={questionLive && !showingAnswer} />
        </h1>
        {/* Soft baseline rule */}
        <div aria-hidden="true" className="mx-auto mt-6 h-px w-24 bg-ink-300/40" />
        {/* Replay control — only when question is settled and not yet answered */}
        {onReplay && !questionLive && !showingAnswer && (
          <ReplayControl onReplay={onReplay} isSpeaking={isSpeaking} />
        )}
      </div>

      {/* Answer slot — slides up from below */}
      <div
        className={`mt-10 w-full max-w-2xl transition-all duration-700 ease-out overflow-hidden ${
          showingAnswer && answerText
            ? "opacity-100 max-h-[400px] translate-y-0"
            : "opacity-0 max-h-0 translate-y-4 pointer-events-none"
        }`}
      >
        {answerText && (
          <div
            className={`transition-all duration-700 ease-out ${
              answerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
            }`}
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-ink-300 mb-3">
              {answerLabel}
            </p>
            <p className="font-display italic text-xl sm:text-2xl leading-[1.55] text-ink-700">
              "{answerText}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ThinkingDots() {
  return (
    <div className="flex gap-2.5 items-center" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-ink-500/60 breath"
          style={{ animationDelay: `${i * 0.32}s` }}
        />
      ))}
    </div>
  );
}

function ReplayControl({ onReplay, isSpeaking }: { onReplay: () => void; isSpeaking: boolean }) {
  return (
    <div className="mt-5 flex justify-center h-5">
      {isSpeaking ? (
        <div
          className="flex items-end gap-[3px] h-5"
          aria-label="Question is being read aloud"
          role="status"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-[3px] bg-ink-500/70 rounded-sm breath origin-bottom"
              style={{
                height: "100%",
                animationDelay: `${i * 0.18}s`,
                animationDuration: "1.1s",
              }}
            />
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={onReplay}
          aria-label="Replay the question"
          className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-ink-300 hover:text-ink-500 transition-colors"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="transition-transform duration-300 group-hover:-rotate-12"
          >
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <path d="M3 4v5h5" />
          </svg>
          Replay
        </button>
      )}
    </div>
  );
}
